import React, { useEffect, useState } from 'react';
import { fetchProducts } from './api';
import { fetchOfferCategories } from './fetchOfferCategories';
import { fetchOffers } from './offersApi';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  useTheme,
  Modal,
  Backdrop,
  Fade,
  Chip,
  Skeleton,
  useMediaQuery,
  Snackbar,
  Alert,
  Tooltip,
  Stack,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency, getCurrencyDecimals, calculateDiscountPercentage } from './currencyHelpers';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TimerIcon from '@mui/icons-material/Timer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PaletteIcon from '@mui/icons-material/Palette';
import { addFavorite, removeFavorite, getFavoriteIds } from './favoritesApi';
import config from './config';
import { useTranslation } from 'react-i18next';

// ✨ Animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translateY(20px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

const heartBeat = keyframes`
  0%, 100% { transform: scale(1); }
  25% { transform: scale(1.3); }
  50% { transform: scale(1.1); }
  75% { transform: scale(1.2); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255, 77, 77, 0.3); }
  50% { box-shadow: 0 0 30px rgba(255, 77, 77, 0.6); }
`;

const slideTransition = keyframes`
  0% { opacity: 0; transform: translateX(30px); }
  100% { opacity: 1; transform: translateX(0); }
`;

// ✅ Image URL processor
const getImageUrls = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';
  
  if (!imageData) {
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.thumbnail && imageData.medium && imageData.large) {
      return {
        thumbnail: imageData.thumbnail.startsWith('http') 
          ? imageData.thumbnail 
          : `${config.SITE_BASE_URL}${imageData.thumbnail}`,
        medium: imageData.medium.startsWith('http') 
          ? imageData.medium 
          : `${config.SITE_BASE_URL}${imageData.medium}`,
        large: imageData.large.startsWith('http') 
          ? imageData.large 
          : `${config.SITE_BASE_URL}${imageData.large}`
      };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'string') {
    let fullUrl;
    if (imageData.startsWith('http')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('data:image')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('/images/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else if (imageData.startsWith('/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else {
      fullUrl = imageData;
    }
    return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
  }
  
  return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
};

// ✅ NEW: Get color image URL
const getColorImageUrl = (colorImage) => {
  if (!colorImage) return null;
  
  if (typeof colorImage === 'object' && colorImage !== null) {
    const url = colorImage.medium || colorImage.large || colorImage.thumbnail;
    if (!url) return null;
    return url.startsWith('http') ? url : `${config.SITE_BASE_URL}${url}`;
  }
  
  if (typeof colorImage === 'string') {
    if (colorImage.startsWith('http')) return colorImage;
    if (colorImage.startsWith('/')) return `${config.SITE_BASE_URL}${colorImage}`;
    return `${config.SITE_BASE_URL}/${colorImage}`;
  }
  
  return null;
};

export default function Offers() {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [products, setProducts] = useState([]);
  const [offerCategories, setOfferCategories] = useState([]);
  const [offers, setOffers] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [autoSlideInterval, setAutoSlideInterval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // ✅ NEW: Color selection state
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  
  const { selectedBranch } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();

  // ✅ Open external product URL helper
  const handleOpenExternalProduct = (prod, e) => {
    if (e) e.stopPropagation();
    const productId = prod.Id || prod.id || prod.ProductId;
    const url = `https://www.elorayem.com/product/${productId}`;
    window.open(url, '_blank');
  };

  useEffect(() => {
    setLoading(true);
    
    fetchProducts(selectedBranch?.id, selectedCurrency).then(setProducts);
    fetchOfferCategories(selectedBranch?.id).then(setOfferCategories);
    
    fetchOffers(selectedBranch?.id, selectedCurrency).then(data => {
      const filtered = data.filter(o => !o.categoryName?.startsWith('مجموعة:') && !o.CategoryName?.startsWith('مجموعة:'));
      setOffers(filtered);
      setLoading(false);
    });
    
    loadFavorites();
  }, [selectedBranch, selectedCurrency]);

  // Auto-slide effect
  useEffect(() => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      
      if (images.length > 1) {
        const interval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        
        setAutoSlideInterval(interval);
        
        return () => {
          clearInterval(interval);
        };
      }
    } else {
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      }
    }
  }, [selectedProduct]);

  const loadFavorites = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const ids = await getFavoriteIds(user.Id || user.id, selectedBranch?.id);
        setFavoriteIds(ids);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleFavorite = async (productId) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showSnackbar(t('auth.loginRequired', 'Please login first'), 'warning');
        return;
      }
      const user = JSON.parse(userStr);
      const userId = user.Id || user.id;

      if (favoriteIds.includes(productId)) {
        await removeFavorite(userId, productId, selectedBranch?.id);
        setFavoriteIds(prev => prev.filter(id => id !== productId));
        showSnackbar(t('favorites.removed', 'Removed from favorites'), 'info');
      } else {
        await addFavorite(userId, productId, selectedBranch?.id);
        setFavoriteIds(prev => [...prev, productId]);
        showSnackbar(t('favorites.added', 'Added to favorites'), 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleImageClick = (imageUrls, e) => {
    e.stopPropagation();
    const largeImageUrl = typeof imageUrls === 'object' ? imageUrls.large : imageUrls;
    setSelectedImage(largeImageUrl);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleProductClick = (prod) => {
    setSelectedProduct(prod);
    setCurrentImageIndex(0);
    
    const colors = prod.ProductColors || prod.productColors;
    if (colors && Array.isArray(colors) && colors.length === 1) {
      setSelectedColor(colors[0].colorName || colors[0].ColorName);
    } else {
      setSelectedColor(null);
    }
    
    const sizes = prod.ProductSizes || prod.productSizes;
    if (sizes && Array.isArray(sizes) && sizes.length === 1) {
      setSelectedSize(sizes[0].sizeName || sizes[0].SizeName || sizes[0].name || sizes[0].Name);
    } else {
      setSelectedSize(null);
    }
  };

  const handleCloseProductModal = () => {
    setSelectedProduct(null);
    setCurrentImageIndex(0);
    setSelectedColor(null);
    setSelectedSize(null);
    if (autoSlideInterval) {
      clearInterval(autoSlideInterval);
      setAutoSlideInterval(null);
    }
  };

  // ✅ Get all product images
  const getAllProductImages = (prod) => {
    const images = [];
    
    const mainImageUrls = getImageUrls(prod.ImageUrl);
    if (mainImageUrls && mainImageUrls.large) {
      images.push(mainImageUrls.large);
    }

    if (prod.ProductImages && Array.isArray(prod.ProductImages) && prod.ProductImages.length > 0) {
      prod.ProductImages.forEach((img) => {
        let imgUrl = null;
        
        if (typeof img === 'object' && img !== null) {
          imgUrl = img.large || img.medium || img.thumbnail;
        } else if (typeof img === 'string') {
          if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:image')) {
            imgUrl = img;
          } else if (img.startsWith('/images/')) {
            imgUrl = `${config.SITE_BASE_URL}${img}`;
          } else if (img.startsWith('/')) {
            imgUrl = `${config.SITE_BASE_URL}${img}`;
          } else {
            imgUrl = `${config.SITE_BASE_URL}/images/${img}`;
          }
        }
        
        if (imgUrl) {
          images.push(imgUrl);
        }
      });
    }

    return images;
  };

  const handleNextImage = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const handlePrevImage = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const toggleAutoSlide = () => {
    if (selectedProduct) {
      const images = getAllProductImages(selectedProduct);
      
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval);
        setAutoSlideInterval(null);
      } else {
        const newInterval = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
        }, 3000);
        setAutoSlideInterval(newInterval);
      }
    }
  };

  const allOfferProducts = offers.flatMap(offer => 
    (offer.products || offer.Products || []).map(prod => {
      const imageData = prod.imageUrl || prod.ImageUrl || prod.imageJson || prod.ImageJson;
      const productId = prod.id || prod.Id;

      return {
        ProductId: productId,
        Id: productId,
        _reactKey: `offer_${offer.offerCategoryId || offer.Id}_${productId}`,
        ProductName: prod.productName || prod.ProductName,
        Price: prod.price || prod.Price,
        OldPrice: prod.oldPrice || prod.OldPrice,
        Currency: prod.currency || prod.Currency,
        CurrencySymbol: prod.currencySymbol || prod.CurrencySymbol || getCurrencySymbol(prod.currency || prod.Currency),
        ImageUrl: imageData,
        Description: prod.description || prod.Description,
        Ingredients: prod.ingredients || prod.Ingredients,
        ProductImages: prod.productImages || prod.ProductImages,
        ProductColors: prod.productColors || prod.ProductColors, 
        ProductSizes: prod.productSizes || prod.ProductSizes,
        categoryName: offer.categoryName || offer.CategoryName,
        CategoryName: offer.categoryName || offer.CategoryName,
        CurrencyPrices: prod.currencyPrices || prod.CurrencyPrices,
        OldCurrencyPrices: prod.oldCurrencyPrices || prod.OldCurrencyPrices,
        OfferCategoryId: offer.offerCategoryId || offer.Id,
        ...prod
      };
    })
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 10, sm: 12, md: 16 }, mb: 20, px: { xs: 1, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width={300} height={60} sx={{ mx: 'auto', mb: 4 }} />
        <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }} sx={{ justifyContent: 'center' }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item key={i}>
              <Skeleton variant="rectangular" width={isMobile ? 140 : 220} height={isMobile ? 280 : 400} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <>
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 10, sm: 12, md: 16 },
          mb: { xs: 4, sm: 6 },
          px: { xs: 1, sm: 3, md: 4 }
        }}
      >
        {/* Header Section */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            mb: { xs: 2.5, sm: 4, md: 5 }, 
            position: 'relative' 
          }}
        >
          <Box sx={{ position: 'relative', textAlign: 'center', px: { xs: 1.5, sm: 5 }, py: { xs: 0.8, sm: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 0.5, sm: 1 } }}>
              <WhatshotIcon sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, color: 'error.main', filter: 'drop-shadow(0 0 10px rgba(255, 77, 77, 0.6))' }} />
              <Typography 
                variant="h4" 
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 800,
                  fontSize: { xs: '1.25rem', sm: '2rem', md: '2.25rem' },
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  position: 'relative',
                  display: 'inline-block',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: { xs: -4, sm: -8 },
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: { xs: '35px', sm: '60px' },
                    height: { xs: '2.5px', sm: '4px' },
                    borderRadius: '2px',
                    background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.4)}`
                  }
                }}
              >
                {t('offers.title')}
              </Typography>
              <LocalOfferIcon sx={{ fontSize: { xs: 32, sm: 40, md: 48 }, color: 'warning.main', filter: 'drop-shadow(0 0 10px rgba(255, 167, 38, 0.6))' }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mt: { xs: 0.3, sm: 0.5 } }}>
              <TimerIcon sx={{ fontSize: { xs: 12, sm: 14, md: 16 }, color: 'error.main' }} />
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                {t('offers.limitedTime', 'Limited Time Only!')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {allOfferProducts.length > 0 ? (
          <Grid 
            container 
            spacing={{ xs: 1, sm: 2.5, md: 3 }} 
            sx={{ 
              direction: 'rtl', 
              justifyContent: 'center' 
            }}
          >
            {allOfferProducts.map((prod, prodIndex) => {
              const imageUrls = getImageUrls(prod.ImageUrl);
              const currentPrice = getPriceInCurrency(prod, selectedCurrency);
              const currencySymbol = getCurrencySymbol(selectedCurrency);
              const decimals = getCurrencyDecimals(selectedCurrency);
              
              let oldPrice = 0;
              let hasDiscount = false;
              
              if (prod.OldPrice) {
                if (prod.OldCurrencyPrices) {
                  oldPrice = getPriceInCurrency({ ...prod, CurrencyPrices: prod.OldCurrencyPrices }, selectedCurrency);
                } else {
                  const priceRatio = prod.OldPrice / (prod.Price || 1);
                  oldPrice = currentPrice * priceRatio;
                }
                hasDiscount = oldPrice > currentPrice;
              }
              
              const discountPercent = hasDiscount ? calculateDiscountPercentage(oldPrice, currentPrice) : 0;
              
              const colors = prod.ProductColors || prod.productColors;
              const hasColors = colors && Array.isArray(colors) && colors.length > 0;
              
              const isFavorite = favoriteIds.includes(prod.ProductId);

              return (
                <Grid 
                  item 
                  key={prod._reactKey || prod.Id}
                  id={`offer-${prod.Id}`}
                  sx={{
                    animation: `${slideIn} 0.5s ease-out`,
                    animationDelay: `${prodIndex * 0.05}s`,
                    animationFillMode: 'backwards'
                  }}
                >
                  <Card 
                    sx={{ 
                      width: { xs: 140, sm: 200, md: 220 },
                      height: '100%',
                      borderRadius: { xs: 1.5, sm: 2 },
                      overflow: 'visible',
                      boxShadow: `0 ${isMobile ? '3px 10px' : '8px 24px'} ${alpha(theme.palette.error.main, 0.12)}`,
                      border: `${isMobile ? '1.5px' : '2px'} solid ${alpha(theme.palette.error.main, 0.15)}`,
                      bgcolor: 'background.paper',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.error.main, 0.02)} 100%)`,
                      '&:hover': { 
                        transform: isMobile ? 'scale(0.98)' : 'translateY(-12px)',
                        boxShadow: `0 ${isMobile ? '5px 14px' : '20px 40px'} ${alpha(theme.palette.error.main, 0.25)}`,
                        border: `${isMobile ? '1.5px' : '2px'} solid ${alpha(theme.palette.error.main, 0.4)}`,
                        '& .product-image': { transform: 'scale(1.1)' }
                      },
                      '&:active': {
                        transform: isMobile ? 'scale(0.95)' : 'translateY(-12px)',
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => handleProductClick(prod)}
                      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}
                    >
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          width: '100%', 
                          height: { xs: 100, sm: 160, md: 180 },
                          overflow: 'hidden',
                          bgcolor: alpha(theme.palette.error.main, 0.03),
                          borderRadius: '12px 12px 0 0'
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={imageUrls.medium}
                          srcSet={`${imageUrls.thumbnail} 150w, ${imageUrls.medium} 400w, ${imageUrls.large} 1200w`}
                          sizes={isMobile ? "140px" : "(max-width: 960px) 200px, 220px"}
                          alt={prod.ProductName}
                          loading="lazy"
                          className="product-image"
                          sx={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover', 
                            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: 'pointer'
                          }}
                          onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                        />

                        <Tooltip title={isFavorite ? t('favorites.remove') : t('favorites.add')} arrow placement="left">
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(prod.ProductId); }}
                            sx={{
                              position: 'absolute',
                              top: { xs: 4, sm: 10 },
                              right: { xs: 4, sm: 10 },
                              bgcolor: 'white',
                              boxShadow: { xs: '0 2px 6px rgba(0,0,0,0.15)', sm: '0 4px 12px rgba(0,0,0,0.15)' },
                              zIndex: 3,
                              width: { xs: 28, sm: 40 },
                              height: { xs: 28, sm: 40 },
                              transition: 'all 0.3s ease',
                              border: `${isMobile ? '1.5px' : '2px'} solid ${isFavorite ? theme.palette.error.main : alpha(theme.palette.text.secondary, 0.2)}`,
                              '&:hover': {
                                bgcolor: 'white',
                                transform: 'scale(1.15)',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                borderColor: theme.palette.error.main
                              },
                              '&:active': {
                                transform: 'scale(0.95)',
                              }
                            }}
                          >
                            {isFavorite ? (
                              <FavoriteIcon sx={{ fontSize: { xs: 16, sm: 22 }, color: theme.palette.error.main, animation: `${heartBeat} 0.3s ease` }} />
                            ) : (
                              <FavoriteBorderIcon sx={{ fontSize: { xs: 16, sm: 22 }, color: theme.palette.text.secondary }} />
                            )}
                          </IconButton>
                        </Tooltip>

                        {hasDiscount && (
                          <Chip
                            icon={<LocalOfferIcon sx={{ fontSize: { xs: 9, sm: 14 }, color: 'white !important' }} />}
                            label={`${discountPercent}%`}
                            sx={{
                              position: 'absolute',
                              top: { xs: 4, sm: 10 },
                              left: { xs: 4, sm: 10 },
                              bgcolor: 'error.main',
                              color: 'white',
                              fontWeight: 800,
                              fontSize: { xs: '9px', sm: '12px' },
                              height: { xs: 20, sm: 28 },
                              boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.error.main, 0.4)}`,
                              zIndex: 2,
                              animation: `${pulse} 2s infinite`,
                              '& .MuiChip-icon': {
                                color: 'white',
                                marginLeft: { xs: '2px', sm: '4px' }
                              }
                            }}
                          />
                        )}

                        {hasColors && (
                          <Tooltip title={`${colors.length} ${t('products.colors', 'colors available')}`} arrow placement="bottom">
                            <Chip
                              icon={<PaletteIcon sx={{ fontSize: { xs: 9, sm: 12 }, color: 'white !important' }} />}
                              label={colors.length}
                              sx={{
                                position: 'absolute',
                                top: hasDiscount ? { xs: 28, sm: 42 } : { xs: 4, sm: 10 },
                                left: { xs: 4, sm: 10 },
                                bgcolor: theme.palette.secondary.main,
                                color: 'white',
                                fontWeight: 800,
                                fontSize: { xs: '8px', sm: '11px' },
                                height: { xs: 18, sm: 24 },
                                boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.secondary.main, 0.4)}`,
                                zIndex: 2,
                                '& .MuiChip-icon': {
                                  color: 'white',
                                  marginLeft: { xs: '2px', sm: '4px' }
                                }
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      
                      <CardContent sx={{ p: { xs: 1, sm: 2 }, flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Tooltip title={prod.ProductName} arrow>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontSize: { xs: '11px', sm: '15px', md: '16px' },
                              fontWeight: 700,
                              color: 'text.primary',
                              mb: { xs: 0.2, sm: 0.5 },
                              textAlign: 'center',
                              lineHeight: 1.3,
                              height: { xs: '2.3em', sm: '2.6em' },
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {prod.ProductName}
                          </Typography>
                        </Tooltip>

                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: { xs: '8px', sm: '11px' },
                            color: 'text.secondary',
                            mb: { xs: 0.8, sm: 1.5 },
                            textAlign: 'center',
                            lineHeight: 1.3,
                            height: { xs: '2.3em', sm: '2.8em' },
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {prod.Description || t('offers.noDescription', 'Limited time offer!')}
                        </Typography>

                        <Box sx={{ flexGrow: 1 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.4, sm: 1 }, mb: { xs: 1, sm: 2 } }}>
                          <Typography sx={{ color: theme.palette.error.main, fontWeight: 800, fontSize: { xs: '12px', sm: '18px' } }}>
                            {currentPrice.toFixed(decimals)} {currencySymbol}
                          </Typography>
                          {hasDiscount && (
                            <Typography sx={{ textDecoration: 'line-through', color: theme.palette.text.disabled, fontSize: { xs: '9px', sm: '12px' }, fontWeight: 500 }}>
                              {oldPrice.toFixed(decimals)} {currencySymbol}
                            </Typography>
                          )}
                        </Box>

                        {/* Action Button: تسوق الان opening https://www.elorayem.com/product/{PRODUCT ID} in new tab */}
                        <Box sx={{ width: '100%', mt: 'auto' }}>
                          {prod.IsActive === false ? (
                            <Box 
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                border: `${isMobile ? '1.5px' : '2px'} solid ${alpha(theme.palette.error.main, 0.3)}`,
                                borderRadius: { xs: 1.5, sm: 3 },
                                py: { xs: 0.8, sm: 1.5 },
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography sx={{ color: theme.palette.error.main, fontWeight: 700, fontSize: { xs: '9px', sm: '13px' }, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                                {t('home.outOfStock', 'Out of Stock')}
                              </Typography>
                            </Box>
                          ) : (
                            <Button 
                              variant="contained"
                              fullWidth
                              onClick={(e) => handleOpenExternalProduct(prod, e)}
                              sx={{ 
                                bgcolor: theme.palette.error.main,
                                borderRadius: { xs: 1.5, sm: 3 },
                                color: 'white',
                                py: { xs: 0.7, sm: 1.2 },
                                fontSize: { xs: '9px', sm: '13px' },
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: `0 ${isMobile ? '2px 6px' : '4px 12px'} ${alpha(theme.palette.error.main, 0.4)}`,
                                '&:hover': {
                                  bgcolor: theme.palette.error.dark,
                                  boxShadow: `0 ${isMobile ? '3px 10px' : '6px 16px'} ${alpha(theme.palette.error.main, 0.5)}`,
                                  transform: isMobile ? 'none' : 'translateY(-2px)'
                                },
                                '&:active': { transform: 'scale(0.95)' },
                                transition: 'all 0.3s ease',
                                gap: { xs: 0.4, sm: 1 }
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: { xs: 12, sm: 18 } }} />
                              تسوق الان
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: { xs: 6, sm: 8 },
              px: { xs: 2, sm: 3 },
              borderRadius: { xs: 3, sm: 4 },
              bgcolor: alpha(theme.palette.error.main, 0.05),
              border: `2px dashed ${alpha(theme.palette.error.main, 0.2)}`
            }}
          >
            <LocalOfferIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.disabled', mb: 2, opacity: 0.3 }} />
            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
              {t('offers.noOffersAvailable', 'No Offers Available')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
              {t('offers.checkBackSoon', 'Check back soon for amazing deals!')}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Product Details Modal */}
      <Modal
        open={!!selectedProduct}
        onClose={handleCloseProductModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: alpha(theme.palette.common.black, 0.75)
          }
        }}
      >
        <Fade in={!!selectedProduct}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: '850px' },
              maxHeight: { xs: '80vh', sm: '82vh', md: '85vh' },
              mt: { xs: 6, sm: 7, md: 8 },
              mb: { xs: 6, sm: 7, md: 8 },
              overflowY: 'auto',
              bgcolor: 'background.paper',
              borderRadius: { xs: 2, sm: 4 },
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`,
              outline: 'none',
              animation: `${slideIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`,
              '&::-webkit-scrollbar': {
                width: '8px'
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: alpha(theme.palette.divider, 0.1),
                borderRadius: '10px'
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.error.main, 0.3),
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.5)
                }
              }
            }}
          >
            {selectedProduct && (() => {
              const images = getAllProductImages(selectedProduct);
              const currentPrice = getPriceInCurrency(selectedProduct, selectedCurrency);
              const currencySymbol = getCurrencySymbol(selectedCurrency);
              const decimals = getCurrencyDecimals(selectedCurrency);
              
              let oldPrice = 0;
              let hasDiscount = false;
              
              if (selectedProduct.OldPrice) {
                if (selectedProduct.OldCurrencyPrices) {
                  oldPrice = getPriceInCurrency(
                    { ...selectedProduct, CurrencyPrices: selectedProduct.OldCurrencyPrices },
                    selectedCurrency
                  );
                } else {
                  const priceRatio = selectedProduct.OldPrice / (selectedProduct.Price || 1);
                  oldPrice = currentPrice * priceRatio;
                }
                hasDiscount = oldPrice > currentPrice;
              }
              
              const discountPercent = hasDiscount ? calculateDiscountPercentage(oldPrice, currentPrice) : 0;
              const isFavorite = favoriteIds.includes(selectedProduct.ProductId);
              const colors = selectedProduct.ProductColors || selectedProduct.productColors;
              const hasColors = colors && Array.isArray(colors) && colors.length > 0;
              
              return (
                <>
                  <IconButton
                    onClick={handleCloseProductModal}
                    sx={{
                      position: 'absolute',
                      top: { xs: 8, sm: 16 },
                      right: { xs: 8, sm: 16 },
                      bgcolor: alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(10px)',
                      zIndex: 10,
                      boxShadow: 3,
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: theme.palette.background.paper,
                        transform: 'rotate(90deg) scale(1.1)',
                        boxShadow: 6
                      },
                      '&:active': {
                        transform: 'scale(0.9)'
                      }
                    }}
                  >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </IconButton>

                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: { xs: 300, sm: 400, md: 450 },
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: alpha(theme.palette.error.main, 0.03),
                            border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`
                          }}
                        >
                          {images.length > 0 ? (
                            <Box
                              component="img"
                              src={images[currentImageIndex]}
                              alt={`${selectedProduct.ProductName} - Image ${currentImageIndex + 1}`}
                              onClick={(e) => handleImageClick(images[currentImageIndex], e)}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                cursor: 'zoom-in',
                                transition: 'opacity 0.6s ease-in-out, transform 0.3s ease',
                                animation: `${fadeIn} 0.6s ease-in-out`,
                                '&:hover': {
                                  transform: 'scale(1.05)'
                                }
                              }}
                              onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                              key={`image-${currentImageIndex}`}
                            />
                          ) : (
                            <Box
                              component="img"
                              src="/logo-storesye.png"
                              alt="No image available"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          )}

                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              right: 16,
                              bgcolor: alpha(theme.palette.common.black, 0.6),
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backdropFilter: 'blur(10px)',
                              cursor: 'zoom-in',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                bgcolor: alpha(theme.palette.common.black, 0.8),
                                transform: 'scale(1.05)'
                              }
                            }}
                            onClick={(e) => images.length > 0 && handleImageClick(images[currentImageIndex], e)}
                          >
                            <ZoomInIcon sx={{ fontSize: 18, color: 'white' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                              {t('products.clickToZoom', 'Click to Zoom')}
                            </Typography>
                          </Box>

                          {images.length > 1 && (
                            <Tooltip 
                              title={autoSlideInterval ? t('products.pauseSlideshow', 'Pause') : t('products.playSlideshow', 'Play')} 
                              arrow
                            >
                              <IconButton
                                onClick={toggleAutoSlide}
                                sx={{
                                  position: 'absolute',
                                  bottom: 16,
                                  left: 16,
                                  bgcolor: alpha(theme.palette.common.black, 0.6),
                                  backdropFilter: 'blur(10px)',
                                  color: 'white',
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.common.black, 0.8),
                                    transform: 'scale(1.1)'
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                              >
                                {autoSlideInterval ? (
                                  <PauseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                ) : (
                                  <PlayArrowIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}

                          {images.length > 1 && (
                            <>
                              <IconButton
                                onClick={handlePrevImage}
                                sx={{
                                  position: 'absolute',
                                  left: { xs: 8, sm: 16 },
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: 3,
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: 6
                                  },
                                  '&:active': {
                                    transform: 'translateY(-50%) scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <NavigateBeforeIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                              </IconButton>
                              <IconButton
                                onClick={handleNextImage}
                                sx={{
                                  position: 'absolute',
                                  right: { xs: 8, sm: 16 },
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                                  backdropFilter: 'blur(10px)',
                                  boxShadow: 3,
                                  width: { xs: 36, sm: 44 },
                                  height: { xs: 36, sm: 44 },
                                  '&:hover': {
                                    bgcolor: theme.palette.background.paper,
                                    transform: 'translateY(-50%) scale(1.1)',
                                    boxShadow: 6
                                  },
                                  '&:active': {
                                    transform: 'translateY(-50%) scale(0.95)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <NavigateNextIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                              </IconButton>
                            </>
                          )}

                          {hasDiscount && (
                            <Chip
                              icon={<LocalOfferIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                              label={`${discountPercent}% ${t('products.off', 'OFF')}`}
                              sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                bgcolor: theme.palette.error.main,
                                color: 'white',
                                fontWeight: 800,
                                fontSize: '14px',
                                height: 32,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
                                zIndex: 2,
                                animation: `${pulse} 2s infinite`,
                                '& .MuiChip-icon': {
                                  color: 'white'
                                }
                              }}
                            />
                          )}

                          <Chip
                            icon={<WhatshotIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                            label={t('offers.hotDeal', 'HOT DEAL')}
                            sx={{
                              position: 'absolute',
                              top: hasDiscount ? 56 : 16,
                              left: 16,
                              bgcolor: theme.palette.warning.main,
                              color: 'white',
                              fontWeight: 800,
                              fontSize: '13px',
                              height: 30,
                              boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.4)}`,
                              zIndex: 2,
                              animation: `${glow} 2s infinite`
                            }}
                          />

                          {images.length > 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                bgcolor: alpha(theme.palette.common.black, 0.6),
                                borderRadius: 2,
                                px: 1.5,
                                py: 0.5,
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <Typography sx={{ fontSize: '0.75rem', color: 'white', fontWeight: 600 }}>
                                {currentImageIndex + 1} / {images.length}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {images.length > 1 && (
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              mt: 2,
                              overflowX: 'auto',
                              pb: 1,
                              '&::-webkit-scrollbar': {
                                height: '6px'
                              },
                              '&::-webkit-scrollbar-track': {
                                bgcolor: alpha(theme.palette.divider, 0.1),
                                borderRadius: '10px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                bgcolor: alpha(theme.palette.error.main, 0.3),
                                borderRadius: '10px',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.5)
                                }
                              }
                            }}
                          >
                            {images.map((img, index) => (
                              <Box
                                key={`thumb-${index}`}
                                onClick={() => {
                                  setCurrentImageIndex(index);
                                  if (autoSlideInterval) {
                                    clearInterval(autoSlideInterval);
                                    const newInterval = setInterval(() => {
                                      setCurrentImageIndex((prev) => (prev + 1) % images.length);
                                    }, 3000);
                                    setAutoSlideInterval(newInterval);
                                  }
                                }}
                                sx={{
                                  minWidth: { xs: 60, sm: 80 },
                                  height: { xs: 60, sm: 80 },
                                  borderRadius: 2,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  border: `3px solid ${index === currentImageIndex ? theme.palette.error.main : 'transparent'}`,
                                  opacity: index === currentImageIndex ? 1 : 0.6,
                                  transition: 'all 0.3s ease',
                                  bgcolor: alpha(theme.palette.error.main, 0.03),
                                  position: 'relative',
                                  '&:hover': {
                                    opacity: 1,
                                    transform: 'scale(1.05)',
                                    boxShadow: 3
                                  },
                                  '&:active': {
                                    transform: 'scale(0.95)'
                                  }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={img}
                                  alt={`Thumbnail ${index + 1}`}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                                />
                                {index === currentImageIndex && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      bottom: 0,
                                      left: 0,
                                      right: 0,
                                      height: '3px',
                                      bgcolor: theme.palette.error.main,
                                      animation: `${slideTransition} 0.3s ease`
                                    }}
                                  />
                                )}
                              </Box>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Stack spacing={{ xs: 2, sm: 3 }}>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                              color: theme.palette.text.primary,
                              mb: 1,
                              lineHeight: 1.2
                            }}
                          >
                            {selectedProduct.ProductName}
                          </Typography>

                          <Chip
                            label={t('offers.specialOffer', 'Special Offer')}
                            size="small"
                            icon={<LocalOfferIcon sx={{ fontSize: 16, color: 'white !important' }} />}
                            sx={{
                              bgcolor: theme.palette.error.main,
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              '& .MuiChip-icon': {
                                color: 'white'
                              }
                            }}
                          />
                        </Box>

                        <Divider />

                        <Box>
                          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontWeight: 900,
                                color: theme.palette.error.main,
                                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }
                              }}
                            >
                              {currentPrice.toFixed(decimals)} {currencySymbol}
                            </Typography>
                            {hasDiscount && (
                              <Typography
                                variant="h5"
                                sx={{
                                  textDecoration: 'line-through',
                                  color: theme.palette.text.disabled,
                                  fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                                  fontWeight: 600
                                }}
                              >
                                {oldPrice.toFixed(decimals)} {currencySymbol}
                              </Typography>
                            )}
                          </Stack>
                          {hasDiscount && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  color: theme.palette.success.main,
                                  fontWeight: 700,
                                  fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                              >
                                {t('products.youSave', 'You save')} {(oldPrice - currentPrice).toFixed(decimals)} {currencySymbol} ({discountPercent}%)
                              </Typography>
                              <WhatshotIcon sx={{ fontSize: 20, color: 'error.main' }} />
                            </Box>
                          )}
                        </Box>

                        <Divider />

                        {/* Colors preview if needed */}
                        {hasColors && (
                          <Box
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              bgcolor: alpha(theme.palette.secondary.main, 0.05),
                              borderRadius: 2,
                              border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                              <PaletteIcon sx={{ fontSize: 24, color: 'secondary.main' }} />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                  color: theme.palette.secondary.main
                                }}
                              >
                                {t('products.selectColor', 'Select Color')}
                              </Typography>
                            </Stack>
                            
                            <RadioGroup
                              value={selectedColor || ''}
                              onChange={(e) => setSelectedColor(e.target.value)}
                            >
                              <Grid container spacing={1.5}>
                                {colors.map((color, index) => {
                                  const colorName = color.colorName || color.ColorName;
                                  const colorImageUrl = getColorImageUrl(color.colorImage || color.ColorImage);
                                  const isSelected = selectedColor === colorName;

                                  return (
                                    <Grid item xs={6} sm={4} key={index}>
                                      <FormControlLabel
                                        value={colorName}
                                        control={<Radio sx={{ display: 'none' }} />}
                                        label={
                                          <Box
                                            sx={{
                                              width: '100%',
                                              height: { xs: 80, sm: 100 },
                                              borderRadius: 2,
                                              overflow: 'hidden',
                                              border: `3px solid ${isSelected ? theme.palette.secondary.main : alpha(theme.palette.secondary.main, 0.2)}`,
                                              cursor: 'pointer',
                                              transition: 'all 0.3s ease',
                                              bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                              position: 'relative',
                                              boxShadow: isSelected ? `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}` : 'none',
                                              '&:hover': {
                                                transform: 'scale(1.05)',
                                                borderColor: theme.palette.secondary.main,
                                                boxShadow: 4
                                              },
                                              '&:active': {
                                                transform: 'scale(0.98)'
                                              }
                                            }}
                                          >
                                            {colorImageUrl ? (
                                              <Box
                                                component="img"
                                                src={colorImageUrl}
                                                alt={colorName}
                                                sx={{
                                                  width: '100%',
                                                  height: '100%',
                                                  objectFit: 'cover'
                                                }}
                                                onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                                              />
                                            ) : (
                                              <Box
                                                sx={{
                                                  width: '100%',
                                                  height: '100%',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  bgcolor: alpha(theme.palette.secondary.main, 0.1)
                                                }}
                                              >
                                                <Typography sx={{ fontSize: '28px' }}>🎨</Typography>
                                              </Box>
                                            )}
                                            
                                            <Box sx={{
                                              position: 'absolute',
                                              bottom: 0,
                                              left: 0,
                                              right: 0,
                                              bgcolor: isSelected 
                                                ? alpha(theme.palette.secondary.main, 0.95)
                                                : alpha(theme.palette.common.black, 0.75),
                                              backdropFilter: 'blur(4px)',
                                              py: 0.5,
                                              textAlign: 'center',
                                              transition: 'all 0.3s ease'
                                            }}>
                                              <Typography
                                                sx={{
                                                  color: 'white',
                                                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                  fontWeight: isSelected ? 700 : 600
                                                }}
                                              >
                                                {colorName}
                                              </Typography>
                                            </Box>
                                            {isSelected && (
                                              <Box
                                                sx={{
                                                  position: 'absolute',
                                                  top: 8,
                                                  right: 8,
                                                  bgcolor: theme.palette.secondary.main,
                                                  borderRadius: '50%',
                                                  width: 24,
                                                  height: 24,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  boxShadow: 3
                                                }}
                                              >
                                                <Typography sx={{ color: 'white', fontSize: '16px' }}>✓</Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        }
                                        sx={{ m: 0, width: '100%' }}
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </RadioGroup>
                          </Box>
                        )}

                        {selectedProduct.Description && (
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1,
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                color: theme.palette.text.primary
                              }}
                            >
                              {t('products.description', 'Description')}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.8,
                                fontSize: { xs: '0.9rem', sm: '1rem' }
                              }}
                            >
                              {selectedProduct.Description}
                            </Typography>
                          </Box>
                        )}

                        {selectedProduct.Ingredients && selectedProduct.Ingredients.length > 0 && (
                          <Box
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              bgcolor: alpha(theme.palette.error.main, 0.05),
                              borderRadius: 2,
                              border: `2px dashed ${alpha(theme.palette.error.main, 0.2)}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <InfoOutlinedIcon sx={{ fontSize: 20, color: 'error.main' }} />
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                  color: theme.palette.error.main
                                }}
                              >
                                {t('products.ingredients', 'Ingredients')}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.6,
                                fontSize: { xs: '0.85rem', sm: '0.95rem' }
                              }}
                            >
                              {Array.isArray(selectedProduct.Ingredients) 
                                ? selectedProduct.Ingredients.join(', ') 
                                : selectedProduct.Ingredients}
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                            borderRadius: 2,
                            border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <TimerIcon sx={{ fontSize: 28, color: 'warning.main' }} />
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: 'warning.dark', fontSize: '0.95rem' }}>
                              {t('offers.limitedTime', 'Limited Time Only!')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                              {t('offers.hurryUp', 'Hurry up before the offer ends!')}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider />

                        {/* Action Buttons */}
                        <Stack spacing={2}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => toggleFavorite(selectedProduct.ProductId)}
                            startIcon={
                              isFavorite ? (
                                <FavoriteIcon sx={{ color: theme.palette.error.main }} />
                              ) : (
                                <FavoriteBorderIcon />
                              )
                            }
                            sx={{
                              borderRadius: 3,
                              py: { xs: 1.2, sm: 1.5 },
                              fontSize: { xs: '0.95rem', sm: '1rem' },
                              fontWeight: 700,
                              borderWidth: 2,
                              borderColor: isFavorite ? theme.palette.error.main : theme.palette.divider,
                              color: isFavorite ? theme.palette.error.main : theme.palette.text.primary,
                              '&:hover': {
                                borderWidth: 2,
                                borderColor: theme.palette.error.main,
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                transform: 'translateY(-2px)',
                                boxShadow: 3
                              },
                              '&:active': {
                                transform: 'scale(0.98)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            {isFavorite ? t('favorites.removeFromFavorites', 'Remove from Favorites') : t('favorites.addToFavorites', 'Add to Favorites')}
                          </Button>

                          {/* تسوق الان Button in modal */}
                          {selectedProduct.IsActive === false ? (
                            <Box
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                borderRadius: 3,
                                py: { xs: 1.5, sm: 2 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.error.main,
                                  fontWeight: 700,
                                  fontSize: { xs: '1rem', sm: '1.15rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {t('home.outOfStock', 'Out of Stock')}
                              </Typography>
                            </Box>
                          ) : (
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={(e) => handleOpenExternalProduct(selectedProduct, e)}
                              startIcon={<OpenInNewIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
                              sx={{
                                bgcolor: theme.palette.error.main,
                                borderRadius: 3,
                                py: { xs: 1.5, sm: 2 },
                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                fontWeight: 700,
                                textTransform: 'none',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.3)}`,
                                '&:hover': {
                                  bgcolor: theme.palette.error.dark,
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha(theme.palette.error.main, 0.4)}`
                                },
                                '&:active': {
                                  transform: 'scale(0.98)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              تسوق الان
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              );
            })()}
          </Box>
        </Fade>
      </Modal>

      {/* Image Zoom Modal */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: {
            backdropFilter: 'blur(12px)',
            backgroundColor: alpha(theme.palette.common.black, 0.85)
          }
        }}
      >
        <Fade in={!!selectedImage}>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '95vw',
              maxHeight: { xs: '80vh', sm: '82vh', md: '85vh' },
              mt: { xs: 6, sm: 7, md: 8 },
              mb: { xs: 6, sm: 7, md: 8 },
              outline: 'none',
              borderRadius: { xs: 2, sm: 4 },
              overflow: 'hidden',
              boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.5)}`,
              animation: `${slideIn} 0.4s cubic-bezier(0.4, 0, 0.2, 1)`
            }}
          >
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                backdropFilter: 'blur(10px)',
                zIndex: 1,
                boxShadow: 3,
                width: { xs: 32, sm: 44 },
                height: { xs: 32, sm: 44 },
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                  transform: 'rotate(90deg) scale(1.1)'
                },
                '&:active': {
                  transform: 'scale(0.9)'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
            </IconButton>
            <Box
              component="img"
              src={selectedImage}
              alt="Product"
              sx={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: { xs: '80vh', sm: '82vh', md: '85vh' },
                objectFit: 'contain',
                borderRadius: { xs: 2, sm: 4 }
              }}
              onError={(e) => { e.target.src = '/logo-storesye.png'; }}
            />
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            boxShadow: 4,
            minWidth: { xs: 200, sm: 300 },
            fontSize: { xs: '0.8rem', sm: '0.95rem' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
